/**
 * CreateWatchlistModalView — presentational watchlist-create dialog.
 *
 * Single text input + Cancel / Create footer, wrapped in an AlertDialog.
 * No business logic — the Container owns mutation state, validation,
 * toast, and navigation. (SE-5007)
 * @module components/CreateWatchlistModal/CreateWatchlistModalView
 */

import { TEST_ID_CREATE_WATCHLIST_MODAL } from "e2e/testId";
import React, { memo } from "react";
import { Platform } from "react-native";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/atoms/AlertDialog";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with transparent backgrounds (!bg-transparent), web:hover overrides, border-r dividers, flex-1…
import { Button, ButtonText } from "@/components/ui/button";
import { Heading } from "@/components/atoms/Heading";
import { Input } from "@/components/atoms/Input";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for the presentational create-watchlist dialog */
interface Props {
  /** Whether the dialog is open */
  readonly isOpen: boolean;
  /** Current value of the name input */
  readonly name: string;
  /** Whether the create mutation is in flight */
  readonly isLoading: boolean;
  /** Whether the Create button can be activated */
  readonly canSubmit: boolean;
  /**
   * Inline error to surface below the input (e.g. backend duplicate-name
   * rejection per Notion 3.1 non-functional requirements). When set, the
   * input becomes invalid and the message appears below it.
   */
  readonly errorMessage?: string;
  /** Fires on input change */
  readonly onChangeName: (value: string) => void;
  /** Fires when the user confirms (Create button or Enter submit) */
  readonly onSubmit: () => void;
  /** Fires when the user dismisses (Cancel only — ESC + backdrop honour platform spec) */
  readonly onClose: () => void;
  /**
   * Fires when the AlertDialog itself requests close (ESC / backdrop tap).
   * Notion 3.1 says backdrop tap on mobile must NOT dismiss to prevent
   * accidental loss of typed input; the View routes that platform decision
   * here so the Container stays oblivious to platform.
   */
  readonly onBackdropDismiss: () => void;
}

/**
 * Renders the create-watchlist AlertDialog with title, subtitle, name input,
 * and Cancel / Create footer.
 * @param props - Component props
 * @param props.isOpen - Whether the dialog is open
 * @param props.name - Current value of the name input
 * @param props.isLoading - Whether the create mutation is in flight
 * @param props.canSubmit - Whether the Create button can be activated
 * @param props.errorMessage - Inline error to surface below the input
 * @param props.onChangeName - Fires on input change
 * @param props.onSubmit - Fires when the user confirms
 * @param props.onClose - Fires when Cancel pressed
 * @param props.onBackdropDismiss - Fires when AlertDialog requests close (ESC / backdrop)
 */
const CreateWatchlistModalView = ({
  isOpen,
  name,
  isLoading,
  canSubmit,
  errorMessage,
  onChangeName,
  onSubmit,
  onClose,
  onBackdropDismiss,
}: Props) => (
  <AlertDialog
    isOpen={isOpen}
    onClose={isLoading ? undefined : onBackdropDismiss}
    useRNModal={Platform.OS !== "web"}
    size="sm"
  >
    <AlertDialogBackdrop className="bg-black" />
    <AlertDialogContent
      testID={TEST_ID_CREATE_WATCHLIST_MODAL.ROOT}
      className="border-0 bg-surface-base p-0 dark:bg-prompt-background"
    >
      <AlertDialogBody className="px-6 pb-5 pt-6">
        <Stack space="5">
          <Stack space="1">
            <Heading
              variant="title"
              className="shrink text-center text-content-primary dark:text-white"
            >
              Create a new watchlist
            </Heading>
            <Text
              variant="body"
              className="shrink text-center leading-snug text-content-muted"
            >
              Create a list first to start adding players
            </Text>
          </Stack>
          <Stack space="2">
            {/*
              The testID belongs on the Input WRAPPER, not inside `field`.
              `field` is spread onto the inner gluestack InputField, and a
              testID there never reaches the iOS accessibility tree: the
              captured hierarchy for this very modal published
              CREATE_WATCHLIST_MODAL.ROOT, .CANCEL_BUTTON and .CREATE_BUTTON
              but no .INPUT — the field appeared only as the generic
              "Input Field" label with an empty resource-id. On the wrapper it
              lands in the atom's `{...props}` instead, which is exactly how
              SIGNIN.INPUT_PHONE is declared, and that id both publishes on iOS
              and is tapped-then-typed-into by the auth flows every night.
            */}
            <Input
              testID={TEST_ID_CREATE_WATCHLIST_MODAL.INPUT}
              variant="outline"
              className="h-12 rounded-none border-outline-default bg-surface-raised dark:border-prompt-border dark:bg-surface-action"
              isDisabled={isLoading}
              isInvalid={Boolean(errorMessage)}
              field={{
                value: name,
                placeholder: "Your list name",
                autoCorrect: false,
                spellCheck: false,
                autoFocus: true,
                onChangeText: onChangeName,
                onSubmitEditing: canSubmit ? onSubmit : undefined,
                returnKeyType: "done",
                className: "text-title-sm text-content-primary dark:text-white",
              }}
            />
            {errorMessage ? (
              <Text
                variant="body"
                className="leading-snug text-needs-semantic-error-600 dark:text-needs-semantic-error-400"
              >
                {errorMessage}
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </AlertDialogBody>
      <AlertDialogFooter className="gap-0 border-t border-outline-default bg-surface-base dark:border-prompt-border dark:bg-prompt-background">
        <Button
          testID={TEST_ID_CREATE_WATCHLIST_MODAL.CANCEL_BUTTON}
          onPress={onClose}
          isDisabled={isLoading}
          className="web:hover:!bg-surface-muted dark:web:hover:!bg-surface-inverse/10 h-12 flex-1 rounded-none border-r border-outline-default !bg-transparent active:!bg-surface-strong dark:border-prompt-border dark:active:!bg-surface-inverse/15"
        >
          <ButtonText className="text-title-sm !text-content-neutral-strong dark:!text-content-subtle">
            Cancel
          </ButtonText>
        </Button>
        <Button
          testID={TEST_ID_CREATE_WATCHLIST_MODAL.CREATE_BUTTON}
          onPress={onSubmit}
          isDisabled={!canSubmit}
          className="web:hover:!bg-surface-muted dark:web:hover:!bg-surface-inverse/10 h-12 flex-1 rounded-none !bg-transparent active:!bg-surface-strong dark:active:!bg-surface-inverse/15"
        >
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <ButtonText
              className={`font-heading text-title-sm ${canSubmit ? "!text-needs-semantic-primary-500 dark:!text-needs-semantic-primary-400" : "!text-content-subtle dark:!text-content-neutral-strong"}`}
            >
              Create
            </ButtonText>
          )}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

CreateWatchlistModalView.displayName = "CreateWatchlistModalView";

export default memo(CreateWatchlistModalView);
